import * as PIXI from 'pixi.js-legacy';

import { SHOW_POPUP_INTRO, SHOW_POPUP_ERROR } from '../settings/events';
import { translate } from '../services/L10n';
import { t } from '../translations';
import emitter from '../emitter';
import tacHelpers from '../state/tac/tacHelpers';
import { floor } from '../util/round';
import constants from '../settings/constants';
import productService from '../services/products';

import Base from './Base';
import getComponent from './util/getComponent';
import { isFixedRoom } from '../util/room';
import idGenerator from '../util/aactools/idGenerator';
import geometry from './util/geometry';
import localStatisticsReporter from '../services/statistics/insights/custom/local/localStatisticsReporter';
import { MeansOfNotification } from '../state/tac/tacReducer/validate';

export default class ItemContainer extends Base {
  constructor(args) {
    super();

    this.space = args.space;
    this.onDragStart = args.onDragStart;
    this.app = args.app;

    if (isFixedRoom()) {
      this.origo = { x: 0, y: 0 };
    }
    this.alphaFilter = new PIXI.filters.AlphaFilter(0.5);
    this.alphaFilter.resolution = 3;
  }

  update({ room, tac, options, ratio, wallResizerActive, updateOrigo = true }) {
    this.filters = wallResizerActive ? [this.alphaFilter] : [];

    const limits = tacHelpers.getLimits(tac, room);
    const width = limits.max.x - limits.min.x;
    this.room = room;
    this.ratio = ratio;
    this.options = options;
    this.tac = tac;

    if (updateOrigo) {
      if (!isFixedRoom()) {
        if (tac.centerConfiguration || !this.origo) {
          this.origo = {
            x: floor(
              limits.min.x - (room.width - width) / 2,
              constants.GRID.x.step
            ),
            y: 0,
          };
        } else {
          if (limits.min.x < this.origo.x) {
            this.origo = {
              x: limits.min.x,
              y: 0,
            };
          } else if (limits.max.x > this.origo.x + room.width) {
            this.origo = {
              x: limits.max.x - room.width,
              y: 0,
            };
          }
        }
      } else {
        this.origo = { x: 0, y: 0 };
      }
    }

    this.eraseDropAreas();

    this.drawChildren(wallResizerActive);

    if (this.options.errorVisible) {
      this.tac.errors.forEach((error, index, array) => {
        if (!constants.SHOW_MULTIPLE_ERRORS && index > 0) {
          return;
        }
        if (
          array.some(error => error.options?.showInstantly) &&
          !error.options?.showInstantly
        ) {
          return;
        }

        const { itemid, translationKey, statisticsLabel } = error;
        const message = translate(translationKey);

        const sprite = this.getSprite(itemid);

        if (sprite) {
          localStatisticsReporter.reportValidationError(
            statisticsLabel,
            MeansOfNotification.Popup
          );

          emitter.emit(SHOW_POPUP_ERROR, sprite, message);
        }
      });
    }
  }

  drawChildren(wallResizerActive) {
    const oldSprites = this.sprites;

    this.sprites = new Map();

    const introPopupIndex = this.tac.items.findIndex(
      item => item.width && item.height
    );

    this.tac.items.forEach((item, index) => {
      let sprite = oldSprites && oldSprites.get(item.itemid);

      if (sprite) {
        if (sprite.eraseDropAreas) {
          sprite.eraseDropAreas();
        }

        sprite.update({
          origo: this.origo,
          parentItem: this.room,
          item,
          ratio: this.ratio,
          options: this.options,
          wallResizerActive,
        });

        oldSprites.delete(item.itemid);

        this.sprites.set(item.itemid, sprite);
      } else {
        sprite = this.addItem(item);

        if (
          this.app.dragging &&
          !idGenerator.hasRealId(item) &&
          productService.isType(item, this.app.dragging.item?.filter.type)
        ) {
          //this is a temporary item that should have the same scale as the one currently being dragged
          const draggingScale = this.app.dragging.sprite?.scale;

          if (draggingScale) {
            sprite.position.x += sprite.width / 2;
            sprite.position.y += sprite.height / 2;
            sprite.pivot.set(sprite.width / 2, sprite.height / 2);
            sprite.scale.set(draggingScale.x, draggingScale.y);
          }
        }
      }

      if (this.options.introPopupsVisible && index === introPopupIndex) {
        setTimeout(() => {
          emitter.emit(
            SHOW_POPUP_INTRO,
            sprite,
            translate(t.BALLOON_HINT_STEP_THREE_WALL_INTRO)
          );
        }, 100);
      }
    });

    if (oldSprites) {
      // remove any remaining old sprites (their items have been removed from the TAC)
      oldSprites.forEach(sprite => sprite.destroy());
    }

    this.sortChildren();
  }

  addItem(item) {
    const { origo, room, options, ratio, onDragStart, app } = this;
    const args = {
      origo,
      parentItem: room,
      item,
      onDragStart,
      ratio,
      options,
      app,
    };

    const sprite = getComponent(args);

    super.addChild(sprite);

    this.sprites.set(item.itemid, sprite);

    return sprite;
  }

  addChild(sprite, sort = true) {
    super.addChild(sprite);

    if (sort) {
      this.sortChildren();
    }
  }
  /**
   * Draws the drop areas for each slot belonging to each parent where each parent is a section.
   * @param {Array<Object>} slots
   */
  drawDropAreas(slots) {
    const parents = Object.values(
      slots
        .map(slot => slot.parent)
        .reduce((out, parent) => {
          out[parent.itemid] = parent;
          return out;
        }, {})
    );

    if (this.canHaveOverlappingDropAreas(slots)) {
      // Currently only applies to boaxel range and the LAGKAPTEN tables.
      slots = this.removeSlotsUnderneathWiderSlots(slots);
      slots = this.adjustDropAreasForTables(parents, slots);
      this.drawDropAreasDefault(parents, slots);
    } else {
      this.drawDropAreasDefault(parents, slots);
    }
  }

  /**
   * Checks if the first slot belongs to an item of type table, then assumes the rest of the slots also belong to the same item.
   * Currently this function will only return true for the boaxel range tables.
   * @param {Array<Object>} slots
   * @returns {boolean} If the slots can have overlapping drop areas, which means they are of type table (LAGKAPTEN) and belong to the boaxel range.
   */
  canHaveOverlappingDropAreas(slots) {
    return productService.canHaveOverlappingDropAreas(slots[0]);
  }

  /**
   * Draws the drop areas for all slots, for each parent.
   * @param {Array<Object>} parents
   * @param {Array<Object>} slots
   */
  drawDropAreasDefault(parents, slots) {
    parents.forEach(parent => {
      const sprite = this.getSprite(parent.itemid);
      const slotsBelongingToParent = this.getSlotsBelongingToParent(
        parent,
        slots
      );
      sprite.drawDropAreas(slotsBelongingToParent);
    });
  }

  /**
   * Adjusts the drop areas by adding an attribute of horizontalOverlap
   * which determines how much the width of the drop-area should be adjusted.
   * @param {Array<Object>} parents
   * @param {Array<Object>} slots
   * @returns {Array<Object>} All slots, some with a horizontalOverlap attribute added.
   */
  adjustDropAreasForTables(parents, slots) {
    parents.sort((a, b) => a.x - b.x); // Sort the sections from the leftmost-placement to the rightmost-placement in the scene.
    const adjustedSlots = this.addHorizontalOverlapToRelevantSlots(
      parents,
      slots
    );
    return adjustedSlots;
  }

  /**
   * Recursively compares the currentParent with the nextParents
   * to determine if there is a horizontal overlap between any of their slots and then adds that horizontalOverlap as an attribute.
   * @param {Array<Object>} param0 A destructured array with the current parent being used as reference and the next parents it should be compared to.
   * @param {Array<Object>} slots The slots that will have a horizontalOverlap attributed added.
   * @returns {Array<Object>} The second parameter, slots, is mutated and then returned.
   */
  addHorizontalOverlapToRelevantSlots([currentParent, ...nextParents], slots) {
    if (!nextParents.length) return slots;
    const currentParentSlots = this.getSlotsBelongingToParent(
      currentParent,
      slots
    );
    nextParents.forEach(nextParent => {
      const nextParentSlots = this.getSlotsBelongingToParent(nextParent, slots);
      currentParentSlots.forEach(currentParentSlot => {
        nextParentSlots.forEach(nextParentSlot =>
          this.addHorizontalOverlapIfItExists(currentParentSlot, nextParentSlot)
        );
      });
    });
    return this.addHorizontalOverlapToRelevantSlots(nextParents, slots);
  }
  /**
   * Compares the currentParentSlot with the nextParentSlot
   * to see if the nextParentSlot overlaps horizontally with the currentParentSlot.
   * If there is an overlap the currentParentSlot receives a horizontalOverlap attribute (representing how much the nextParentSlot overlaps with the currentParentSlot)
   * The attribute is then used in the DropArea class to adjust the width of the relevant slots drop-area.
   * @param {Object} currentParentSlot
   * @param {Object} nextParentSlot
   */
  addHorizontalOverlapIfItExists(currentParentSlot, nextParentSlot) {
    if (
      this.isOverlappingPartiallyHorizontally(currentParentSlot, nextParentSlot)
    ) {
      const horizontalOverlap = this.getHorizontalOverlap(
        currentParentSlot,
        nextParentSlot
      );
      if (!currentParentSlot.horizontalOverlap)
        currentParentSlot.horizontalOverlap = horizontalOverlap;
      else if (horizontalOverlap > currentParentSlot.horizontalOverlap)
        currentParentSlot.horizontalOverlap = horizontalOverlap;
    }
  }
  /**
   * If two slots have the same x and y-coordinates it will remove the slot with the smaller width.
   * @example Assume the drop-areas (s1 and s2) are on-top of eachother.
   *          |s1---------s1|
   *          |s2----s2| This slot is considered to be underneath s1 and will be removed.
   * @param {Array<Object>} slots
   * @returns {Array<Object>} All slots without the slots that are underneath other slots.
   */
  removeSlotsUnderneathWiderSlots(slots) {
    const slotsUnderneath = this.getSlotsUnderneathWiderSlots([], slots);
    return slots.filter(slot => !slotsUnderneath.includes(slot));
  }

  /**
   * Gets the slots that are considered to be underneath other slots.
   * @param {Array<Object>} slotsUnderneath The slots that have the same x and y-coordinates as well as are overlapped entirely by another drop-area.
   * @param {Array<Object>} param1 This array is used and sent in as a paremeter recursively to compare the slot at the first index with the rest of the slots.
   * @returns {Array<Object>} The slots that are considered to be underneath other slots.
   */
  getSlotsUnderneathWiderSlots(slotsUnderneath, [currentItem, ...restArr]) {
    if (!restArr.length) return slotsUnderneath;

    const filteredSlotsUnderneath = restArr.filter(item => {
      return (
        this.slotsHaveSameCoordinates(item, currentItem) &&
        this.getSlotWithShortestWidth(item, currentItem)
      );
    });
    return filteredSlotsUnderneath.length
      ? this.getSlotsUnderneathWiderSlots(
          [...slotsUnderneath, ...filteredSlotsUnderneath],
          restArr
        )
      : this.getSlotsUnderneathWiderSlots(slotsUnderneath, restArr);
  }

  /**
   * Gets the slot with the shortest width of the two that are compared.
   * @param {Object} slot1
   * @param {Object} slot2
   * @returns {Object} The slot with the shortest width.
   */
  getSlotWithShortestWidth(slot1, slot2) {
    return slot1.width < slot2.width ? slot1 : slot2;
  }

  /**
   * Checks if the slots have the same x, y and z-coordinates.
   * @param {Object} slot1
   * @param {Object} slot2
   * @returns {boolean} If the slots have the same x, y and z-coordinates.
   */
  slotsHaveSameCoordinates(slot1, slot2) {
    return slot1.x === slot2.x && slot1.y === slot2.y && slot1.z === slot2.z;
  }

  /**
   * Gets all of the slots belonging to a specific parent/item.
   * @param {Object} parent
   * @param {Array<Object>} slots
   * @returns {Array<Object>} The slots that belong to the parent.
   */
  getSlotsBelongingToParent(parent, slots) {
    return slots.filter(slot => {
      return slot.parent.itemid === parent.itemid;
    });
  }

  /**
   * Checks to see if two slots partially overlap horizontally.
   * @example |s1-------|s2--s1|----s2| Slot1 partially overlaps with Slot2.
   * @example |s1s2-----s2|--s1|
   * @param {Object} slot1
   * @param {Object} slot2
   * @returns {boolean} If the two slots partially overlap.
   */
  isOverlappingPartiallyHorizontally(slot1, slot2) {
    return geometry.collides(slot1, slot2) && slot1.y === slot2.y;
  }

  /**
   * Gets the horizontal overlap between two slots by checking the width of the intersecting rectangle.
   * @param {Object} slot1
   * @param {Object} slot2
   * @returns {number} The width of the overlap in mm.
   */
  getHorizontalOverlap(slot1, slot2) {
    return geometry.intersectingRect(slot1, slot2).width;
  }

  eraseDropAreas() {
    this.children.forEach(sprite => {
      if (sprite.item && sprite.eraseDropAreas) {
        sprite.eraseDropAreas();
      }
    });
  }

  /**
   *
   * @param {*} item
   * @returns {Object | undefined} Sprite bounds, or undefined if not on scene
   */
  getItemBounds(item) {
    const sprite = this.getSprite(item.itemid);

    if (!sprite) {
      return;
    }

    sprite.toggleMultiParentChildren && sprite.toggleMultiParentChildren();
    const bounds = sprite.getBounds();
    sprite.toggleMultiParentChildren && sprite.toggleMultiParentChildren();
    return bounds;
  }

  getSprite(itemid) {
    if (itemid?.itemid) {
      // this was an item, not an id
      itemid = itemid.itemid;
    }
    return this.getFromMap(this.sprites, itemid);
  }

  getFromMap(sprites, itemid) {
    if (!sprites || !sprites.size) {
      return;
    }
    if (sprites.has(itemid)) {
      return sprites.get(itemid);
    }
    for (const item of sprites.entries()) {
      if (Array.isArray(item) && item[1]) {
        const found = this.getFromMap(item[1].sprites, itemid);
        if (found) {
          return found;
        }
      }
    }
    return;
  }
}
